import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type ConsistencyWarningStatus = 'open' | 'dismissed' | 'resolved';

export interface ConsistencyWarningDto {
  id: string;
  imageId: string;
  entityId: string;
  entityName?: string | null;
  warningType: string;
  score: number | null;
  descriptorsMissing: string[];
  status: ConsistencyWarningStatus;
  createdAt: string;
  resolvedAt?: string | null;
}

export interface BookGapsDto {
  bookId: string;
  paragraphsMissingImages: number;
  paragraphsMissingAudio: number;
  paragraphsMissingMetadata: number;
  chaptersMissingCover: number;
  entitiesMissingReferenceImage: number;
  totalParagraphs: number;
  missingImageParagraphIds: string[];
  missingAudioParagraphIds: string[];
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useConsistencyWarnings(filters: {
  entityId?: string;
  bookId?: string;
  status?: ConsistencyWarningStatus;
}) {
  const { getToken } = useAuth();
  return useQuery<ConsistencyWarningDto[]>({
    queryKey: ['consistency-warnings', filters],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const search = new URLSearchParams();
      if (filters.entityId) search.set('entity_id', filters.entityId);
      if (filters.bookId) search.set('book_id', filters.bookId);
      if (filters.status) search.set('status', filters.status);
      const response = await client
        .get(`api/v2/consistency/warnings?${search.toString()}`)
        .json<ApiEnvelope<ConsistencyWarningDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useDismissWarning() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (warningId: string) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/consistency/warnings/${warningId}/dismiss`);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['consistency-warnings'] }),
  });
}

export function useBookGaps(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<BookGapsDto>({
    enabled: Boolean(bookId),
    queryKey: ['book-gaps', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/books/${bookId}/gaps`)
        .json<ApiEnvelope<BookGapsDto>>();
      return response.data;
    },
  });
}
